import {
  setGtmDefaultConsent,
  updateGtmConsentState,
} from '@redbrickmedia/duplex.user-identity'
import { getCookieClient } from '../utils/storage/utils'
import { consentSnapshot } from '../utils/consent/consentGate'
import { COHORT_COOKIE } from '../openfeature/utils'
import type { GtagEvent } from '../types/assertive'
import debug from '../utils/console'
import {
  markConsentTiming,
  measureConsentTiming,
} from '../utils/consent/consentTiming'

const logger = debug.withTag('plugins:google-tag-manager')

export default defineNuxtPlugin({
  name: 'google-tag-manager',
  dependsOn: ['openfeature', 'assertive'],
  setup: async (nuxtApp) => {
    markConsentTiming('gtm-plugin-start')
    const { googleTagManagerId, redesignedNtpPaths } = useAppConfig()
    const { gtag, initialize } = useGtag()
    const route = useRoute()

    const { client } = await useOpenFeature()

    if (!googleTagManagerId) {
      logger.warn('Google Tag Manager: Gtag ID not available in app config')
      return
    }

    // route is reactive and the app is interactive before the callback runs.
    const initialPath = route.path

    // GTM still initialises only after the manager, chained rather than awaited.
    void Promise.resolve(nuxtApp.$ayLoaded).then(() => {
      markConsentTiming('gtm-initialize-start')
      // Google Consent Mode v2. The denied default has to sit in the dataLayer
      // ahead of the container's own `config` command, because gtag.js replays
      // the queue in order, and a default landing after `config` lets the first
      // pageview through unconsented.
      //
      // nuxt-gtag issues `js`/`config` itself, but only on the branch where it
      // finds no dataLayer. Seeding the queue to get the consent default in
      // first takes that branch away, so the two commands it would have sent
      // are sent here instead. `initialize()` is still what injects the script.
      window.dataLayer = window.dataLayer || []
      // `arguments`, not rest params: gtag.js expects the arguments object, and
      // this matches both Google's snippet and nuxt-gtag's own internal shim.
      window.gtag =
        window.gtag ??
        function gtagShim() {
          // gtag.js reads the arguments object; a rest array is not the same
          // shape. Mirrors Google's snippet and nuxt-gtag's own shim.
          // eslint-disable-next-line prefer-rest-params
          window.dataLayer.push(arguments)
        }
      setGtmDefaultConsent()
      window.gtag('js', new Date())
      window.gtag('config', googleTagManagerId, {})

      initialize(googleTagManagerId)
      markConsentTiming('gtm-initialize-end')
      measureConsentTiming(
        'gtm-initialize',
        'gtm-initialize-start',
        'gtm-initialize-end'
      )

      // Recover a terminal decision that landed before gtag was installed. Later
      // decisions and revisions are pushed by the consent package itself.
      const currentConsent = consentSnapshot()
      if (currentConsent && currentConsent.state !== 'unknown') {
        updateGtmConsentState(currentConsent.state === 'allow')
      }

      // Maintain window.dataLayer field for legacy pages
      if (!import.meta.dev && !redesignedNtpPaths.includes(initialPath)) {
        gtag('js', new Date())
        gtag('config', 'UA-62117061-22')
      }
    })

    nuxtApp.hook('app:mounted', () => {
      try {
        const { $ayManagerEnv } = useNuxtApp()
        window.googletag = window.googletag || { cmd: [] }

        window.googletag.cmd.push(() => {
          // Always set page-level targeting
          const cohortCookie = getCookieClient(COHORT_COOKIE)

          window.googletag.setConfig({
            targeting: {
              page_type: getPageType(),
              split_test_name: client.getStringValue(
                'splittestname',
                'default'
              ),
              cohort: cohortCookie ?? 'unknown',
              ay_version: String(
                $ayManagerEnv?.versionInfo?.versionId ?? 'unknown'
              ),
            },
          })

          // Conditionally add pubads event listener for native fallback
          const nativeFallbackEnabled = client.getBooleanValue(
            'nativeFallbackEnabled',
            true
          )
          if (!nativeFallbackEnabled) {
            logger.info('Native ad fallback disabled via feature flag')
            return
          }

          if (!window.googletag.pubads) {
            logger.error(
              'googletag.pubads() not available - cannot register native fallback listener'
            )
            return
          }

          const nativeAdProvider = client.getStringValue(
            'nativeAdProvider',
            'outbrain'
          )

          window.googletag
            .pubads()
            .addEventListener('slotRenderEnded', (event: GtagEvent) =>
              setupNativeAdAsFallback(event, nativeAdProvider)
            )
        })
      } catch (err) {
        logger.error('Error mounting event listeners for native fallback', err)
      }
    })
  },
})
